import { closetAuthHeaders } from '@/lib/closet/client-auth'
import type { Outfit } from '@/types/closet'

/**
 * Browser-side wrappers over the /api/closet routes. Every write goes through
 * `closetAuthHeaders()` so the server's `enforceClosetAuth` guard sees the
 * credential; callers never assemble auth headers themselves.
 */

/** Throws with the route's own error text, so callers surface a real reason. */
async function assertOk(response: Response, action: string) {
  if (response.ok) return
  const detail = await response.text().catch(() => '')
  throw new Error(
    `Failed to ${action}: ${response.status}${detail ? ` ${detail}` : ''}`,
  )
}

export async function deleteClosetItem(id: string) {
  const response = await fetch(`/api/closet/items/${id}`, {
    method: 'DELETE',
    headers: closetAuthHeaders(),
  })
  await assertOk(response, 'delete item')
  return true
}

// Outfit operations
export async function fetchOutfits() {
  const response = await fetch('/api/closet/outfits')
  await assertOk(response, 'fetch outfits')

  const data = await response.json()
  return data.outfits as Outfit[]
}

export async function deleteOutfit(id: string) {
  const response = await fetch(`/api/closet/outfits/${id}`, {
    method: 'DELETE',
    headers: closetAuthHeaders(),
  })
  await assertOk(response, 'delete outfit')
  return true
}

/**
 * Clears the cached "outfit of the day" in Redis. Called after deleting the
 * outfit that was serving as it, so the next request recomputes instead of
 * serving a dangling reference.
 */
export async function clearOutfitOfDay() {
  const response = await fetch('/api/closet/outfit-of-day', {
    method: 'DELETE',
    headers: closetAuthHeaders(),
  })
  await assertOk(response, 'clear outfit of the day')
  return true
}

export async function updateLookMedia(
  id: string,
  action: 'image' | 'rotation' | 'accept' | 'discard',
  candidateId?: string,
): Promise<Outfit> {
  const review = action === 'accept' || action === 'discard'
  const response = await fetch(
    `/api/closet/outfits/${encodeURIComponent(id)}/${action === 'rotation' ? 'generate-video' : 'media'}`,
    {
      method: review ? 'PATCH' : 'POST',
      headers: { ...closetAuthHeaders(), 'Content-Type': 'application/json' },
      body: review ? JSON.stringify({ action, candidateId }) : undefined,
    },
  )
  await assertOk(response, 'update look media')
  return (await response.json()).outfit as Outfit
}

export async function saveOutfit(
  body: Record<string, unknown>,
): Promise<Outfit> {
  const response = await fetch('/api/closet/outfits', {
    method: 'POST',
    headers: closetAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  })
  await assertOk(response, 'save outfit')
  return (await response.json()).outfit as Outfit
}

export async function prepareOutfitMedia(id: string) {
  await updateLookMedia(id, 'image')
  return updateLookMedia(id, 'rotation')
}
